/**
 * Chat auto-scroll policy (PPL-534).
 *
 * f8 chat pinned its message list to the bottom on every render that changed
 * `messages` or `isLoading`. For a plain text bubble that is right — the bottom
 * of the list IS the new message. For a bubble carrying skill cards it is not:
 * a draft, a people list or a circle card is taller than the viewport, so
 * "scroll to the bottom" parks the reader at the end of a card whose first line
 * they never saw, and the next re-render yanks it away again mid-read.
 *
 * The policy is a pure function so it can be checked without a DOM
 * (chatAutoScroll.check.ts):
 *   - a scroll the user themselves caused (sending, the typing indicator)
 *     always goes to the bottom;
 *   - otherwise, when the newest message carries cards, a first arrival brings
 *     the TOP of that message into view and every repeat trigger is skipped;
 *   - with no cards, everything scrolls to the bottom as before.
 *
 * The app carries the same policy in app/src/screens/Tabs/chatAutoScroll.ts.
 * Keep the two in step; they cannot import each other.
 */

/** What made us consider scrolling. */
export type AutoScrollTrigger =
  /** A new message was appended (snapshot listener, or a bot reply we wrote). */
  | 'message_arrived'
  /** A thread's messages were just loaded into the list. */
  | 'thread_loaded'
  /** The list's content size changed (a bubble or card finished laying out). */
  | 'content_resized'
  /** The on-screen keyboard opened or closed (mobile web). */
  | 'keyboard'
  /** The user sent something, or the typing indicator appeared. */
  | 'composer';

/** What the list should do. */
export type AutoScrollMode =
  /** Scroll to the bottom — the historical behaviour. */
  | 'end'
  /** Bring the top of the newest message into view. */
  | 'message_top'
  /** Leave the scroll position alone. */
  | 'skip';

/** Only the part of a chat message this policy looks at. */
export interface AutoScrollMessage {
  cards?: unknown;
}

/**
 * True when a message carries at least one renderable card. Mirrors the filter
 * the renderer uses (`card && typeof card === 'object' && card.type`), so an
 * empty array — or junk — counts as "no cards", exactly as on screen.
 */
export function hasRenderableCards(message?: AutoScrollMessage | null): boolean {
  if (!message) return false;
  const cards = message.cards;
  if (!Array.isArray(cards)) return false;
  return cards.some(
    (card) =>
      !!card &&
      typeof card === 'object' &&
      typeof (card as { type?: unknown }).type === 'string' &&
      (card as { type: string }).type.length > 0,
  );
}

/** The scroll decision for one trigger, given the newest message in the list. */
export function autoScrollMode(
  trigger: AutoScrollTrigger,
  newestMessage?: AutoScrollMessage | null,
): AutoScrollMode {
  // The user just acted at the composer — go where they expect.
  if (trigger === 'composer') return 'end';
  if (!hasRenderableCards(newestMessage)) return 'end';
  if (trigger === 'message_arrived' || trigger === 'thread_loaded') return 'message_top';
  return 'skip';
}
